import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Users, Plus, Building2 } from "lucide-react";
import { toast } from "sonner";
import RowActions from "../../components/RowActions";
import { useSchools, SchoolSelect } from "../../components/SchoolSelect";

const EMPTY = { student_id: "", route_id: "", stop_index: 0, direction: "both", fee_amount: 0, notes: "" };

export default function TransportAssignmentsPage() {
  const { t } = useI18n();
  const { schools, schoolId, setSchoolId, schoolName } = useSchools();
  const [items, setItems] = useState([]);
  const [students, setStudents] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [filterRoute, setFilterRoute] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => { if (schoolId) api.get(`/transport-assignments?school_id=${schoolId}`).then(r => setItems(r.data || [])); };
  useEffect(() => {
    if (!schoolId) return;
    load();
    setFilterRoute("all");
    api.get(`/students?school_id=${schoolId}`).then(r => setStudents(r.data || []));
    api.get(`/routes?school_id=${schoolId}`).then(r => setRoutes(r.data || []));
  }, [schoolId]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...EMPTY, ...a }); setOpen(true); };
  const save = async () => {
    try {
      const payload = { ...form, school_id: editing ? (editing.school_id || schoolId) : schoolId, stop_index: Number(form.stop_index) || 0, fee_amount: form.fee_amount === "" ? null : Number(form.fee_amount) };
      if (editing) await api.put(`/transport-assignments/${editing.id}`, payload);
      else await api.post("/transport-assignments", payload);
      toast.success(t("success")); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (a) => {
    try { await api.delete(`/transport-assignments/${a.id}`); toast.success(t("success")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const selectedRoute = routes.find(r => r.id === form.route_id);
  const stops = selectedRoute?.stops || [];
  const filtered = filterRoute === "all" ? items : items.filter(i => i.route_id === filterRoute);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-3"><Users className="w-7 h-7 text-primary" /> {t("transport_assignments")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("students_enrolled")}</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <SchoolSelect schools={schools} value={schoolId} onChange={setSchoolId} testId="assignments-school-select" />
          <Select value={filterRoute} onValueChange={setFilterRoute}>
            <SelectTrigger className="w-56" data-testid="assignment-filter-route"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("routes")}</SelectItem>
              {routes.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button onClick={openNew} data-testid="new-assignment-btn"><Plus className="w-4 h-4 me-2" />{t("new_assignment")}</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? t("edit") : t("new_assignment")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="text-xs text-muted-foreground bg-muted/40 border border-border rounded-lg px-3 py-2 flex items-center gap-2" data-testid="assignment-school-context">
                  <Building2 className="w-3.5 h-3.5" /> {t("school")}: <b className="text-foreground">{schoolName(editing?.school_id || schoolId) || "—"}</b>
                </div>
                <div><Label>{t("students")}</Label>
                  <Select value={form.student_id} onValueChange={v => setForm({ ...form, student_id: v })}>
                    <SelectTrigger data-testid="assignment-student"><SelectValue /></SelectTrigger>
                    <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.full_name || s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>{t("routes")}</Label>
                  <Select value={form.route_id} onValueChange={v => setForm({ ...form, route_id: v, stop_index: 0 })}>
                    <SelectTrigger data-testid="assignment-route"><SelectValue /></SelectTrigger>
                    <SelectContent>{routes.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {stops.length > 0 && (
                  <div><Label>{t("stop_name")}</Label>
                    <Select value={String(form.stop_index)} onValueChange={v => setForm({ ...form, stop_index: Number(v) })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {stops.map((s, i) => <SelectItem key={i} value={String(i)}>{i + 1}. {s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label>{t("direction")}</Label>
                  <Select value={form.direction} onValueChange={v => setForm({ ...form, direction: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">{t("morning")}</SelectItem>
                      <SelectItem value="evening">{t("evening")}</SelectItem>
                      <SelectItem value="both">{t("both")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>{t("fees") || "Fee"}</Label><Input type="number" value={form.fee_amount} onChange={e => setForm({ ...form, fee_amount: e.target.value })} /></div>
                <Button onClick={save} className="w-full" data-testid="assignment-save">{t("save")}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="border shadow-none">
        <ul className="divide-y divide-border">
          {filtered.map(a => {
            const stu = students.find(s => s.id === a.student_id);
            const route = routes.find(r => r.id === a.route_id);
            const stop = (route?.stops || [])[a.stop_index];
            return (
              <li key={a.id} className="p-4 flex items-center gap-3 hover:bg-muted/30 smooth" data-testid={`assignment-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{stu?.full_name_ar || stu?.full_name || stu?.name || "—"}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
                    <Building2 className="w-3 h-3" /> {schoolName(a.school_id) || "—"} · {route?.name || "—"} · {stop?.name || "—"} · {t(a.direction)} {a.fee_amount ? `· ${a.fee_amount} SAR` : ""}
                  </div>
                </div>
                <RowActions item={a} onEdit={openEdit} onDelete={remove} testIdPrefix="assignment" />
              </li>
            );
          })}
          {filtered.length === 0 && <li className="p-12 text-center text-muted-foreground">{t("no_data")}</li>}
        </ul>
      </Card>
    </div>
  );
}
